/**
 * Bitrix24 renders a limited BBCode subset inside chat custom-data blocks
 * (support-chat identity) and open-line messages, so any value that
 * originates from user input — an org name, a page title, most of all —
 * must not be able to introduce markup. `[url=...]` is the sharpest edge:
 * left unescaped it turns an operator's context panel into a link the
 * customer chose.
 *
 * The bracket escaping (`&#91;`/`&#93;`) only round-trips back to literal
 * brackets if Bitrix decodes HTML entities before interpreting BBCode — i.e.
 * it assumes an HTML sink downstream. Under that same assumption `&`, `<`,
 * `>` pass through unescaped unless we escape them too, so this function
 * escapes the full HTML set. `&` MUST be escaped first: escaping it after
 * `<`/`>`/`[`/`]` would re-escape the entities those replacements just
 * introduced (`&lt;` -> `&amp;lt;`).
 *
 * Pure string helper, no Node/DOM APIs — safe in both the edge-api Worker
 * and the ui-worker browser bundle.
 */
export function escapeBbcode(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\[/g, '&#91;')
    .replace(/\]/g, '&#93;');
}

/**
 * Text for a Bitrix24 field that renders BBCode and does NOT decode HTML
 * entities (`im.notify`, `im.message.add`, timeline comments): a zero-width
 * space after every `[` keeps the text as written while no tag is recognized —
 * so a user-entered name cannot become `[URL=…]` in someone's notification.
 * `escapeBbcode` above is for an HTML sink; here its entities would show up
 * literally.
 */
export function neutralizeBbcode(text: string): string {
  return text.replace(/\[/g, '[\u200B');
}

/**
 * Text Bitrix24 IM would otherwise link by itself: it turns a bare
 * `scheme://…` or `www.…` into a link. A zero-width space after the `:` and
 * after `www` keeps the text as written and leaves nothing to link — so a
 * user-entered name cannot become a link beside the app's own in someone's
 * notification.
 */
export function defuseAutolinks(text: string): string {
  return text
    .replace(/:\/\//g, ':\u200B//')
    .replace(/\bwww\./gi, (match) => `${match.slice(0, 3)}\u200B.`);
}
